<template>
  <div :class="$style.base">
    <div :class="$style.date" v-if="firstDateObject && !isUnavailable">
      {{ $t('availabilities.first_available_on') }}
      <span
        :class="$style.link"
        :style="{ color: widgetSettings.color }"
        @click="onDateClick"
        v-test="'firstDate'"
        >{{ $d(firstDateObject, 'full') }}</span
      >
    </div>
    <IconSpinner v-if="isLoading" />
    <div v-if="isUnavailable && employeeId" v-test="'employee-unavailable'">
      {{ $t('availabilities.unavailable') }}
      <span v-if="employeeSelectionRequired">
        {{ $t('availabilities.select_other_employee') }}
      </span>
      <div v-else>
        <span
          :class="$style.link"
          :style="{ color: widgetSettings.color }"
          @click="onResetClick"
          v-test="'reset'"
          >{{ $t('availabilities.unavailable_reset_link') }}</span
        >
      </div>
    </div>
    <div v-if="isUnavailable && !employeeId" v-test="'unavailable-all'">
      {{ $t('availabilities.unavailable_all') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, nextTick, ref, watch } from 'vue';

import { useAvailabilityStore } from '@/stores/availability';
import { useBookingStore } from '@/stores/booking';
import { useCompanyStore } from '@/stores/company';
import widgetSettings from '@/widget-settings';
import { loadFirstAvailability } from './load-first-availability';

import IconSpinner from '@/components/IconSpinner.vue';

const fetchedDate = ref<string | null>(null);
const isUnavailable = ref(false);
const isLoading = ref(false);

const { addEmptyDates } = useAvailabilityStore();
const { availabilities } = storeToRefs(useAvailabilityStore());
const { fetchAvailabilities } = useAvailabilityStore();

const { employeeSelectionRequired } = useCompanyStore();
const { employeeId, date, locationId, serviceIds } = storeToRefs(
  useBookingStore()
);

const firstDate = computed(() => {
  if (availabilities.value.length) {
    return (
      availabilities.value.find((item) => {
        let isValid = false;
        if (item.timeslots?.length) {
          isValid = employeeId.value
            ? item.timeslots.some((slot) =>
                slot.resource_ids.includes(employeeId.value as number)
              )
            : true;
        }
        return isValid;
      }) || null
    );
  }
  return null;
});

const firstDateObject = computed(() => {
  return firstDate.value
    ? new Date(`${firstDate.value.date}T00:00`)
    : fetchedDate.value
      ? new Date(`${fetchedDate.value}T00:00`)
      : null;
});

const onDateClick = () => {
  if (firstDate.value) {
    date.value = firstDate.value.date;
  } else if (fetchedDate.value) {
    addEmptyDates(`${fetchedDate.value}T00:00`);

    fetchAvailabilities();

    nextTick(() => {
      if (fetchedDate.value) {
        date.value = fetchedDate.value;
      }
    });
  }
};

const onResetClick = () => {
  isUnavailable.value = false;
  employeeId.value = null;
};

const fetchDate = () => {
  isLoading.value = true;
  loadFirstAvailability({
    locationId: locationId.value as string,
    serviceIds: serviceIds.value,
    resourceId: employeeId.value
  })
    .then((data) => {
      if (data.first) {
        fetchedDate.value = data.first;
      } else if (!widgetSettings.bookingId) {
        isUnavailable.value = true;
      }
    })
    .catch((error) => {
      isUnavailable.value = true;
      console.warn(error);
    })
    .finally(() => {
      isLoading.value = false;
    });
};

watch(
  employeeId,
  () => {
    isUnavailable.value = false;
    fetchedDate.value = null;

    nextTick(() => {
      if (!firstDate.value) {
        fetchDate();
      }
    });
  },
  { immediate: true }
);

watch(
  firstDate,
  () => {
    if (firstDate) {
      isUnavailable.value = false;
    }
  },
  { immediate: true }
);
</script>

<style lang="scss" module>
.base {
  position: relative;
  min-height: 20px;
  margin-top: $spacing;
  text-align: center;
}
.link {
  @include link;
}
</style>
